import { useState } from 'react';
import { Plus } from 'lucide-react';
import { type RoundForm, buildRoundBody, isRoundValid, useAddInvestmentRound } from './useAddInvestmentRound';

const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';
const INPUT =
  'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink';

/**
 * Modal de novo aporte (follow-on). Acrescenta uma rodada ao investimento; o backend soma ao cheque
 * total. POST /api/investments/{id}/rounds (If-Match). Valor em BRL + data do aporte.
 */
export function FollowOnModal({
  id,
  etag,
  codename,
  onClose,
}: {
  id: string;
  etag: string;
  codename: string;
  onClose: () => void;
}) {
  const addRound = useAddInvestmentRound();
  const [form, setForm] = useState<RoundForm>({
    amountBRL: '',
    deployedAt: new Date().toISOString().slice(0, 10),
  });

  const canConfirm = isRoundValid(form) && !addRound.isPending;

  const onConfirm = () => {
    if (!canConfirm) return;
    addRound.mutate({ id, etag, body: buildRoundBody(form) }, { onSuccess: onClose });
  };

  return (
    <>
      <button
        type="button"
        aria-label="Cancelar"
        onClick={onClose}
        className="fixed inset-0 z-[90] cursor-default bg-[rgba(20,18,12,0.45)]"
      />
      <div
        role="dialog"
        aria-label="Registrar aporte"
        className="fixed left-1/2 top-1/2 z-[100] w-[min(420px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-vega-surface p-5 shadow-vega-lg"
      >
        <div className="mb-1 flex items-center gap-2">
          <Plus size={16} className="text-vega-ink-2" />
          <h2 className="text-[15px] font-semibold text-vega-ink">Registrar aporte</h2>
        </div>
        <p className="mb-4 text-[12.5px] text-vega-muted-2">
          Novo follow-on em <span className="text-vega-ink">{codename}</span>. Soma ao cheque total.
        </p>

        <label className="mb-3 block">
          <span className={LABEL}>Valor (BRL) *</span>
          <input
            value={form.amountBRL}
            onChange={(e) => setForm((f) => ({ ...f, amountBRL: e.target.value }))}
            inputMode="decimal"
            autoFocus
            placeholder="ex.: 50000000"
            className={INPUT}
          />
        </label>

        <label className="mb-4 block">
          <span className={LABEL}>Data do aporte *</span>
          <input
            type="date"
            value={form.deployedAt}
            onChange={(e) => setForm((f) => ({ ...f, deployedAt: e.target.value }))}
            className={INPUT}
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={addRound.isPending}
            className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-muted disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
          >
            {addRound.isPending ? 'Salvando…' : 'Registrar aporte'}
          </button>
        </div>
      </div>
    </>
  );
}
